import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  TemplateRef,
  ViewEncapsulation,
  model,
  signal,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';

export type PopoverTrigger = 'hover' | 'click';
export type PopoverVariant = 'default' | 'pointer';

export type PopoverPosition =
  'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'center';

export interface PopoverConfig {
  template?: TemplateRef<unknown>;
  content?: string;
}

@Component({
  selector: 'hx-popover',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './popover.html',
  styleUrls: ['./popover.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'cssStyle()',
  },
})
export class HxPopover extends HxBaseControl {
  template = signal<TemplateRef<unknown> | undefined>(undefined);
  content = signal<string | undefined>(undefined);
  variant = signal<'default' | 'pointer'>('default');
  position = signal<PopoverPosition>('bottom');
  override cssStyle = model<string>('');
}

import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, OnDestroy, OnInit, ViewContainerRef, inject, input } from '@angular/core';

@Directive({
  selector: '[hxPopover]',
  standalone: true,
})
export class HxPopoverDirective implements OnInit, OnDestroy {
  private overlayRef?: OverlayRef;
  private popoverComponentRef?: ComponentRef<HxPopover>;
  private showTimeout?: number;
  private hideTimeout?: number;

  private overlay = inject(Overlay);
  private vcr = inject(ViewContainerRef);
  private host = inject(ElementRef<HTMLElement>);

  private escListener = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.hide();
    }
  };

  private triggerMouseEnter = () => this.showWithDelay();
  private triggerMouseLeave = () => this.hideWithDelay();
  private overlayMouseEnter = () => this.clearHideTimeout();
  private overlayMouseLeave = () => this.hideWithDelay();

  // Signal inputs
  hxPopover = input<string | TemplateRef<unknown> | undefined>(undefined);
  trigger = input<PopoverTrigger>('hover');
  /** When enabled, hover lifecycle follows the configured trigger input. */
  useTriggerForHoverLifecycle = input<boolean>(false);
  delay = input<number>(100);
  backdrop = input<boolean>(false);
  appearance = input<'default' | 'pointer'>('default');
  position = input<PopoverPosition>('bottom');
  accessible = input<boolean>(false);
  cssStyle = input<string>('');
  offset = input<number | undefined>(undefined);

  /**
   * Whether to close the popover when scrolling and the trigger element goes out of viewport bounds. If false, the
   * popover will reposition to follow the trigger element as you scroll.
   *
   * @default true
   */
  closeOnScroll = input<boolean>(true);

  ngOnInit() {
    this.bindTriggers();
  }

  private bindTriggers() {
    const el = this.host.nativeElement;

    if (this.trigger() === 'hover') {
      el.addEventListener('mouseenter', this.triggerMouseEnter);
      el.addEventListener('mouseleave', this.triggerMouseLeave);
    } else if (this.trigger() === 'click') {
      el.addEventListener('click', () => this.toggle());
    }
  }

  private createOverlay() {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.host)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withPositions(this.mapPositions());

    // Listen to position changes to update arrow direction
    positionStrategy.positionChanges.subscribe((position) => {
      if (this.overlayRef?.hasAttached()) {
        const componentRef = this.overlayRef.overlayElement.querySelector('.hx-popover');
        if (componentRef) {
          // Determine actual position from CDK overlay position
          const actualPosition = this.getActualPosition(position);
          this.popoverComponentRef?.instance.position.set(actualPosition);
          componentRef.setAttribute('data-position', actualPosition);
        }
      }
    });

    // Use 'close' or 'reposition' scroll strategy based on closeOnScroll input
    const scrollStrategy = this.closeOnScroll()
      ? this.overlay.scrollStrategies.close()
      : this.overlay.scrollStrategies.reposition();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: this.backdrop(),
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    if (this.backdrop()) {
      this.overlayRef.backdropClick().subscribe(() => this.hide());
    }
  }

  /** Determine the actual position based on CDK overlay's chosen position */
  private getActualPosition(position: any): PopoverPosition {
    const originY = position.connectionPair.originY;
    const originX = position.connectionPair.originX;
    const overlayY = position.connectionPair.overlayY;
    const overlayX = position.connectionPair.overlayX;

    // Center position (overlay directly on top of origin)
    if (originY === 'top' && overlayY === 'top' && originX === 'start' && overlayX === 'start') {
      return 'center';
    }

    // Top positions (overlay below origin)
    if (originY === 'top' && overlayY === 'bottom') {
      if (originX === 'start' && overlayX === 'start') return 'top-start';
      if (originX === 'end' && overlayX === 'end') return 'top-end';
      return 'top';
    }

    // Bottom positions (overlay above origin)
    if (originY === 'bottom' && overlayY === 'top') {
      if (originX === 'start' && overlayX === 'start') return 'bottom-start';
      if (originX === 'end' && overlayX === 'end') return 'bottom-end';
      return 'bottom';
    }

    // Left position (overlay to right of origin)
    if (originX === 'start' && overlayX === 'end') return 'left';

    // Right position (overlay to left of origin)
    if (originX === 'end' && overlayX === 'start') return 'right';

    // Default to bottom
    return 'bottom';
  }

  private showWithDelay() {
    this.clearHideTimeout();
    this.showTimeout = window.setTimeout(() => this.show(), this.delay());
  }

  private hideWithDelay() {
    clearTimeout(this.showTimeout);
    this.hideTimeout = window.setTimeout(() => this.hide(), this.delay());
  }

  private clearHideTimeout() {
    clearTimeout(this.hideTimeout);
  }

  private show() {
    if (!this.overlayRef) {
      this.createOverlay();
    }

    if (!this.overlayRef!.hasAttached()) {
      const portal = new ComponentPortal(HxPopover, this.vcr);
      const ref = this.overlayRef!.attach(portal);
      this.popoverComponentRef = ref;

      const content = this.hxPopover();
      if (content instanceof TemplateRef) {
        ref.instance.template.set(content);
      } else {
        ref.instance.content.set(content);
      }

      ref.instance.variant.set(this.appearance());
      ref.instance.position.set(this.position());

      // Set the cssStyle if provided
      if (this.cssStyle()) {
        ref.instance.cssStyle.set(this.cssStyle());
      }

      if (this.trigger() === 'hover') {
        this.overlayRef!.overlayElement.addEventListener('mouseenter', this.overlayMouseEnter);
        this.overlayRef!.overlayElement.addEventListener('mouseleave', this.overlayMouseLeave);
      }

      /** ✅ Attach ESC listener only when visible */
      if (this.accessible()) {
        document.addEventListener('keydown', this.escListener);
      }

      /** ✅ For hover trigger, keep popover open when hovering over it */
      const hoverLifecycleTrigger = this.useTriggerForHoverLifecycle() ? this.trigger() : this.popoverTrigger();
      if (hoverLifecycleTrigger === 'hover') {
        this.attachOverlayHoverListeners();
      }
    }
  }

  /** Attach mouseenter/mouseleave to overlay to keep it open when hovering over content */
  private attachOverlayHoverListeners() {
    if (!this.overlayRef) return;

    const overlayElement = this.overlayRef.overlayElement;

    const onOverlayEnter = () => {
      clearTimeout(this.hideTimeout);
    };

    const onOverlayLeave = () => {
      this.hideWithDelay();
    };

    overlayElement.addEventListener('mouseenter', onOverlayEnter);
    overlayElement.addEventListener('mouseleave', onOverlayLeave);

    // Store cleanup function for later
    const cleanup = () => {
      overlayElement.removeEventListener('mouseenter', onOverlayEnter);
      overlayElement.removeEventListener('mouseleave', onOverlayLeave);
    };

    // Store cleanup on the overlay element for removal during hide/destroy
    (overlayElement as any)._hxPopoverCleanup = cleanup;
  }

  public hide() {
    if (this.trigger() === 'hover' && this.overlayRef) {
      this.overlayRef.overlayElement.removeEventListener('mouseenter', this.overlayMouseEnter);
      this.overlayRef.overlayElement.removeEventListener('mouseleave', this.overlayMouseLeave);
    }

    this.overlayRef?.detach();
    this.popoverComponentRef = undefined;

    /** ✅ Clean up ESC listener */
    if (this.accessible()) {
      document.removeEventListener('keydown', this.escListener);
    }
  }

  private toggle() {
    this.overlayRef?.hasAttached() ? this.hide() : this.show();
  }

  ngOnDestroy() {
    if (this.trigger() === 'hover') {
      const el = this.host.nativeElement;
      el.removeEventListener('mouseenter', this.triggerMouseEnter);
      el.removeEventListener('mouseleave', this.triggerMouseLeave);
    }
    this.hide();
    this.overlayRef?.dispose();
  }

  /**
   * Maps the popover position to CDK ConnectedPosition configurations. CDK Overlay automatically handles viewport
   * overflow with withPush() and withFlexibleDimensions(). We only need primary position + opposite fallback.
   */
  private mapPositions(): ConnectedPosition[] {
    const offset = this.offset() ?? 8; // Gap between trigger and popover (customizable via offset input)
    const position = this.position();

    const positionMap: Record<PopoverPosition, ConnectedPosition> = {
      top: { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offset },
      bottom: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offset },
      left: { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -offset },
      right: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offset },
      'top-start': { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -offset },
      'top-end': { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -offset },
      'bottom-start': { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: offset },
      'bottom-end': { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: offset },
      center: { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: -10, offsetY: -10 },
    };

    // Get opposite fallback position
    const fallbackMap: Record<PopoverPosition, PopoverPosition> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
      'top-start': 'bottom-start',
      'top-end': 'bottom-end',
      'bottom-start': 'top-start',
      'bottom-end': 'top-end',
      center: 'center',
    };

    const primary = positionMap[position];
    const fallback = positionMap[fallbackMap[position]];

    // Return primary position + opposite fallback (CDK handles the rest)
    return [primary, fallback];
  }

  popoverTrigger = signal<PopoverTrigger>('hover'); // Default value can be 'hover' or 'click'
}
